import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { canOverride, eventTimeLabel } from '@cerebro-diem/core';
import type { CalendarEvent } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { calendarKeys, setEventBlocks } from '../../services/calendar';
import { chipClass } from '../../components/ContainerChip';
import { outlineButton } from '../../components/styles';

/**
 * One calendar event, read-only: when, what, which calendar, and whether it
 * makes its time busy. An event on a Mixed calendar can be told it does not
 * block (and back).
 */
export default function CalendarEventItem({ event, day, timeZone }: { event: CalendarEvent; day: string; timeZone?: string }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => setEventBlocks(event.id, event.blocks ? false : null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: calendarKeys.all }),
  });

  return (
    <div
      className="rounded-lg border-l-4 bg-surface-variant/40 p-2 space-y-1"
      style={{ borderLeftColor: event.calendar_color ?? 'currentColor' }}
      data-event-id={event.id}
    >
      <p className="text-xs text-on-surface-variant">
        {eventTimeLabel(event, day, timeZone)} · {event.calendar_name}
      </p>
      <p className="text-sm font-medium break-words">{event.title}</p>
      {event.location && (
        <p className="text-xs text-on-surface-variant inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" aria-hidden="true" />
          {event.location}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`${chipClass} ${event.blocks ? 'bg-error/10 text-error' : 'bg-surface-variant text-on-surface-variant'}`}>
          {event.blocks ? 'Busy' : 'FYI'}
        </span>
        {canOverride(event) && (
          <button
            type="button"
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
            className={`${outlineButton} text-xs`}
          >
            {event.blocks ? 'Not busy' : 'Busy after all'}
          </button>
        )}
      </div>
      {toggle.isError && <p role="alert" className="text-xs text-error">Not saved: {errorMessage(toggle.error)}</p>}
    </div>
  );
}
